import { useState, useCallback, useEffect } from 'react';
import { extractReferences } from '../utils/referenceParser';

/**
 * Hook for handling references in articles.
 * Kept in features as the domain-level source of truth.
 */
function articleContentToPlainText(articleContent) {
  const blocks = Array.isArray(articleContent?.blocks) ? articleContent.blocks : [];
  const lines = [];

  blocks.forEach((block) => {
    if (typeof block?.data?.text === 'string') {
      lines.push(block.data.text);
      return;
    }
    if (Array.isArray(block?.data?.items)) {
      lines.push(block.data.items.join(' '));
    }
  });

  return lines.join('\n');
}

export const useReferenceHandler = (articleContent, workspaceId, options = {}) => {
  const { enableRegexFallback = false } = options;
  const [extractedReferences, setExtractedReferences] = useState([]);
  const [selectedReference, setSelectedReference] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!enableRegexFallback) {
      setExtractedReferences([]);
      return;
    }

    const plainText = articleContentToPlainText(articleContent);
    if (!plainText.trim()) {
      setExtractedReferences([]);
      return;
    }

    setExtractedReferences(extractReferences(plainText));
    return;
  }, [articleContent, workspaceId, enableRegexFallback]);

  const handleReferenceClick = useCallback((reference) => {
    setSelectedReference(reference);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedReference(null);
  }, []);

  const getReferencesByType = useCallback(() => {
    const grouped = {
      numbered_with_source: [],
      bracketed_source: [],
      numbered_only: []
    };

    extractedReferences.forEach((ref) => {
      if (grouped[ref.type]) {
        grouped[ref.type].push(ref);
      }
    });

    return grouped;
  }, [extractedReferences]);

  const getUniqueFilenames = useCallback(() => {
    const filenames = new Set();
    extractedReferences.forEach((ref) => {
      if (ref.filename) {
        filenames.add(ref.filename);
      }
    });
    return Array.from(filenames);
  }, [extractedReferences]);

  const searchReferences = useCallback((query) => {
    if (!query) return extractedReferences;

    const lowerQuery = query.toLowerCase();
    return extractedReferences.filter((ref) =>
      (ref.filename && ref.filename.toLowerCase().includes(lowerQuery))
      || (ref.displayText && ref.displayText.toLowerCase().includes(lowerQuery))
      || (ref.original && ref.original.toLowerCase().includes(lowerQuery))
    );
  }, [extractedReferences]);

  return {
    extractedReferences,
    selectedReference,
    drawerOpen,
    handleReferenceClick,
    closeDrawer,
    getReferencesByType,
    getUniqueFilenames,
    searchReferences,
    totalReferences: extractedReferences.length,
    uniqueFiles: getUniqueFilenames().length
  };
};
